import React, { createRef, useEffect, useMemo, useState } from 'react'
import PropTypes from 'prop-types'
import moment from 'moment'
import { Element, scroller } from 'react-scroll'
import AdminDashboardBanner from 'components/AdminDashboardBanner'
import AdminSortButton from 'components/AdminSortButton'
import DrawerComponent from 'components/DrawerComponent'
import { Tier2ClassHeader } from 'components/admin/ClassDrawerHeader'
import TierLabel from 'components/TierLabel'
import K8Class from 'pages/K8Class'
import {
  ORDER_BY,
  TIER2_LESSON_COMPLETION_SORT_KEYS,
  TIER2_SMALL_GROUP_STATUS,
} from 'store/reportsManager/constants'
import { getGradeBandLabel } from 'store/reportsManager/helpers'
import {
  formatCountLabel,
  formatHoursSinceTimestamp,
} from 'utils/reportsHelpers'
import { pluralizeEnglish } from 'utils/stringHelpers'
import { TOOLTIP_LOCATIONS } from 'utils/tooltipHelpers'
import DesktopView from 'components/admin/DesktopView'
import {
  BodyWrapper,
  PageWrapper,
  ContentPanelWrapper,
  StyledSortableAccordionList,
  Row,
  Cell,
  Name,
  Group,
  HeaderTitleRow,
  HeaderTitle,
  LastUpdatedAtText,
  TierPill,
  SummaryRow,
  Pipe,
  YellowBullet,
  FiltersRow,
  FiltersLabel,
  FilterCheckboxGroup,
  FilterCheckboxLabel,
  StyledFilterTooltip,
  NoMatchesMessage,
} from './component.styles'

const K8Tier2LessonCompletion = ({
  classDetailsReport,
  classInstance,
  currentPage,
  gradeBand,
  handleOpenClassDrawer,
  handlePaginationSortChange,
  lastUpdatedAt,
  orderBy,
  pageSize,
  selectedProgram,
  siteId,
  sortKey,
  sortedAndPaginatedLessonCompletionData,
  totalItems,
  totalPages,
  incompleteGroupsCount,
}) => {
  if (!siteId) return null

  const closeButtonRef = createRef(null)
  const [isOpen, setIsOpen] = useState(!!classInstance)
  const [fixedView, setFixedView] = useState(false)
  const [fixedTop, setFixedTop] = useState(false)
  const [showActiveGroupsFilter, setShowActiveGroupsFilter] = useState(false)
  const [showHiddenGroupsFilter, setShowHiddenGroupsFilter] = useState(false)

  // Sync drawer state with classInstance from URL
  useEffect(() => {
    setIsOpen(!!classInstance)
  }, [classInstance])

  const handleScroll = e => {
    const offsetTop = e.target.scrollTop
    if (offsetTop > 22) setFixedTop(true)
    else if (fixedTop) setFixedTop(false)
    if (offsetTop > 52) setFixedView(true)
    else if (fixedView) setFixedView(false)
  }

  const handleCloseDrawer = event => {
    if (
      event.type === 'click' ||
      event.key === 'Enter' ||
      event.key === 'Escape'
    ) {
      event.preventDefault()
      handleOpenClassDrawer(null, siteId)
    }
  }

  useEffect(() => {
    const onKeyPress = e => {
      if (e.key === 'Escape' && isOpen) {
        handleCloseDrawer(e)
      }
    }

    if (isOpen) {
      window.addEventListener('keyup', onKeyPress)
    }

    return () => {
      window.removeEventListener('keyup', onKeyPress)
    }
  }, [isOpen])

  const formatDate = dateString => {
    if (!dateString) return '—'
    return moment(dateString).format('MM/DD/YYYY')
  }

  const buildSortButtons = () => {
    const onSortClick = (clickedSortKey /*, currentSortOrder */) => {
      const isSame = sortKey === clickedSortKey
      const nextOrder = isSame
        ? orderBy === ORDER_BY.ASC
          ? ORDER_BY.DESC
          : ORDER_BY.ASC
        : ORDER_BY.ASC

      scroller.scrollTo('scroll-to-header', {
        duration: 200,
        delay: 50,
        smooth: true,
        offset: 60,
      })

      handlePaginationSortChange({
        newSortKey: clickedSortKey,
        newOrderBy: nextOrder,
        newPage: 1,
      })
    }

    const programKey = selectedProgram?.key || 'K8'
    const snapshotSource = 'progressMonitoringSnapshot'

    return [
      {
        sortKey: TIER2_LESSON_COMPLETION_SORT_KEYS.SMALL_GROUPS,
        title: 'Small Groups',
        handleOnClick: onSortClick,
        tooltipLocation: TOOLTIP_LOCATIONS.TIER2_TABLE_SMALL_GROUPS,
        programKey,
        snapshotSource,
      },
      {
        sortKey: TIER2_LESSON_COMPLETION_SORT_KEYS.STATUS,
        title: 'Status',
        handleOnClick: onSortClick,
      },
      {
        sortKey: TIER2_LESSON_COMPLETION_SORT_KEYS.LESSONS_DONE,
        title: 'Lessons Done',
        handleOnClick: onSortClick,
        tooltipLocation: TOOLTIP_LOCATIONS.TIER2_TABLE_LESSONS_DONE,
        programKey,
        snapshotSource,
      },
      {
        sortKey: TIER2_LESSON_COMPLETION_SORT_KEYS.FIRST_UNIT,
        title: 'Emotions',
        handleOnClick: onSortClick,
        tooltipLocation: TOOLTIP_LOCATIONS.TIER2_TABLE_EMOTIONS,
        programKey,
        snapshotSource,
      },
      {
        sortKey: TIER2_LESSON_COMPLETION_SORT_KEYS.SECOND_UNIT,
        title: 'Relationships',
        handleOnClick: onSortClick,
        tooltipLocation: TOOLTIP_LOCATIONS.TIER2_TABLE_RELATIONSHIPS,
        programKey,
        snapshotSource,
      },
      {
        sortKey: TIER2_LESSON_COMPLETION_SORT_KEYS.THIRD_UNIT,
        title: 'Exec. Function',
        handleOnClick: onSortClick,
        tooltipLocation: TOOLTIP_LOCATIONS.TIER2_TABLE_EXECUTIVE_FUNCTION,
        programKey,
        snapshotSource,
      },
      {
        sortKey: TIER2_LESSON_COMPLETION_SORT_KEYS.LAST_LOGGED_IN,
        title: 'Last Logged In',
        handleOnClick: onSortClick,
      },
    ]
  }

  const hasBaseDataArray = Array.isArray(sortedAndPaginatedLessonCompletionData)

  const filtersActive = showActiveGroupsFilter || showHiddenGroupsFilter

  const filteredLessonCompletionData = useMemo(() => {
    if (!hasBaseDataArray) {
      return sortedAndPaginatedLessonCompletionData
    }

    // If no filters are selected, or both are selected, show all items
    if (!filtersActive || (showActiveGroupsFilter && showHiddenGroupsFilter)) {
      return sortedAndPaginatedLessonCompletionData
    }

    return sortedAndPaginatedLessonCompletionData.filter(item => {
      const status = item.isFinished
        ? TIER2_SMALL_GROUP_STATUS.HIDDEN
        : TIER2_SMALL_GROUP_STATUS.ACTIVE

      if (showActiveGroupsFilter) {
        return status === TIER2_SMALL_GROUP_STATUS.ACTIVE
      }

      if (showHiddenGroupsFilter) {
        return status === TIER2_SMALL_GROUP_STATUS.HIDDEN
      }

      return true
    })
  }, [
    hasBaseDataArray,
    sortedAndPaginatedLessonCompletionData,
    filtersActive,
    showActiveGroupsFilter,
    showHiddenGroupsFilter,
  ])

  const hasAnyGroups =
    hasBaseDataArray &&
    (sortedAndPaginatedLessonCompletionData?.length || 0) > 0

  const hasFilteredResults =
    Array.isArray(filteredLessonCompletionData) &&
    filteredLessonCompletionData.length > 0

  const shouldShowNoMatchesMessage =
    hasAnyGroups && filtersActive && !hasFilteredResults

  const renderEmptyMessage = () => {
    if (shouldShowNoMatchesMessage) {
      return <NoMatchesMessage>No matches: copy TBD</NoMatchesMessage>
    }
    return null
  }

  const renderHeader = () => {
    const gradeBandLabel = getGradeBandLabel(gradeBand)

    const hasIncomplete = (incompleteGroupsCount || 0) > 0
    const incompleteCopy = hasIncomplete
      ? `${pluralizeEnglish(incompleteGroupsCount, 'group', 'groups')} ${
          incompleteGroupsCount === 1 ? "hasn't" : "haven't"
        } completed a lesson`
      : ''

    return (
      <>
        <HeaderTitleRow>
          <HeaderTitle>{gradeBandLabel}</HeaderTitle>
          <TierPill>
            <TierLabel labelText={'TIER 2'} />
          </TierPill>
        </HeaderTitleRow>
        <SummaryRow>
          <span>{formatCountLabel(totalItems || 0, 'Group')}</span>
          {hasIncomplete && (
            <>
              <Pipe>|</Pipe>
              <YellowBullet />
              <span>{incompleteCopy}</span>
            </>
          )}
        </SummaryRow>
      </>
    )
  }

  const formattedLastUpdatedAt = formatHoursSinceTimestamp(lastUpdatedAt)
  const renderFilters = () => {
    return (
      <DesktopView breakpoint={1100}>
        <FiltersRow>
          <FiltersLabel>Filter:</FiltersLabel>
          <FilterCheckboxGroup>
            <FilterCheckboxLabel htmlFor="tier2-filter-active-groups">
              <input
                checked={showActiveGroupsFilter}
                id="tier2-filter-active-groups"
                onChange={() =>
                  setShowActiveGroupsFilter(prevShow => !prevShow)
                }
                type="checkbox"
              />
              <span>Active Groups</span>
              <StyledFilterTooltip
                dataTestId="tier2-filter-active-groups-tooltip"
                label="Active Groups"
                location={TOOLTIP_LOCATIONS.TIER2_FILTER_ACTIVE_GROUPS}
                programKey={selectedProgram?.key || 'K8'}
                snapshotSource="progressMonitoringSnapshot"
              />
            </FilterCheckboxLabel>
            <FilterCheckboxLabel htmlFor="tier2-filter-hidden-groups">
              <input
                checked={showHiddenGroupsFilter}
                id="tier2-filter-hidden-groups"
                onChange={() =>
                  setShowHiddenGroupsFilter(prevShow => !prevShow)
                }
                type="checkbox"
              />
              <span>Hidden Groups</span>
              <StyledFilterTooltip
                dataTestId="tier2-filter-hidden-groups-tooltip"
                label="Hidden Groups"
                location={TOOLTIP_LOCATIONS.TIER2_FILTER_HIDDEN_GROUPS}
                programKey={selectedProgram?.key || 'K8'}
                snapshotSource="progressMonitoringSnapshot"
              />
            </FilterCheckboxLabel>
          </FilterCheckboxGroup>
        </FiltersRow>
      </DesktopView>
    )
  }

  return (
    <PageWrapper>
      <BodyWrapper>
        <AdminDashboardBanner selectedProgram={selectedProgram} />

        <ContentPanelWrapper>
          <LastUpdatedAtText>{formattedLastUpdatedAt}</LastUpdatedAtText>
          <Element name="scroll-to-header" />
          <StyledSortableAccordionList
            currentItemCount={
              Array.isArray(filteredLessonCompletionData)
                ? filteredLessonCompletionData.length
                : 0
            }
            currentPage={currentPage}
            desktopViewBreakpoint={1100}
            handlePageChange={newPage => {
              scroller.scrollTo('scroll-to-header', {
                duration: 200,
                delay: 50,
                smooth: true,
                offset: 60,
              })
              handlePaginationSortChange({ newPage })
            }}
            itemLabel="Group"
            items={filteredLessonCompletionData}
            itemsPerPage={pageSize}
            renderBelowHairline={renderFilters}
            renderEmptyMessage={renderEmptyMessage}
            renderLeftBox={renderHeader}
            renderSortButton={props => <AdminSortButton {...props} />}
            showExportButton={false}
            showHairline
            showTable={!shouldShowNoMatchesMessage}
            sortButtons={buildSortButtons()}
            sortKey={sortKey}
            sortOrder={orderBy}
            suppressDesktopViewMessage
            totalItems={totalItems}
            totalPages={totalPages}
          >
            {item => (
              <Row
                key={`${item.classId || ''}-${item.groupId ||
                  ''}-${item.teacherEmail || Math.random()}`}
                onClick={() =>
                  handleOpenClassDrawer(item.classInstance, siteId)
                }
                onKeyDown={e => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault()
                    handleOpenClassDrawer(item.classInstance, siteId)
                  }
                }}
                role="button"
                tabIndex={0}
              >
                <Cell>
                  <Name>
                    {item.teacherFirstName} {item.teacherLastName}
                  </Name>
                  <Group>{item.groupName}</Group>
                </Cell>
                <Cell>
                  {item.isFinished
                    ? TIER2_SMALL_GROUP_STATUS.HIDDEN
                    : TIER2_SMALL_GROUP_STATUS.ACTIVE}
                </Cell>
                <Cell>{item.lessonsDone ?? 0}</Cell>
                <Cell>{item.units?.[0]?.lessonsDone ?? 0}</Cell>
                <Cell>{item.units?.[1]?.lessonsDone ?? 0}</Cell>
                <Cell>{item.units?.[2]?.lessonsDone ?? 0}</Cell>
                <Cell>{formatDate(item.lastLoggedIn)}</Cell>
              </Row>
            )}
          </StyledSortableAccordionList>
        </ContentPanelWrapper>
      </BodyWrapper>
      <DrawerComponent
        direction="right"
        handleCloseDrawer={handleCloseDrawer}
        handleScroll={handleScroll}
        isOpen={isOpen}
        ref={closeButtonRef}
      >
        <K8Class
          HeaderComponent={Tier2ClassHeader}
          classDetailsReport={classDetailsReport}
          fixedTop={fixedTop}
          fixedView={fixedView}
          headerProps={{
            classTitle: classDetailsReport?.className || '',
            fixedTop,
            fixedView,
            gradeName: gradeBand ? getGradeBandLabel(gradeBand) : '',
            lessonsCompleted: classDetailsReport?.lessonsCompleted || 0,
            teacherFirstName: classDetailsReport?.teacherFirstName || '',
            teacherLastName: classDetailsReport?.teacherLastName || '',
          }}
          variant="tier2"
        />
      </DrawerComponent>
    </PageWrapper>
  )
}

K8Tier2LessonCompletion.propTypes = {
  classDetailsReport: PropTypes.object,
  classInstance: PropTypes.string,
  currentPage: PropTypes.number,
  gradeBand: PropTypes.string,
  handleOpenClassDrawer: PropTypes.func,
  handlePaginationSortChange: PropTypes.func,
  incompleteGroupsCount: PropTypes.number,
  lastUpdatedAt: PropTypes.string,
  orderBy: PropTypes.string,
  pageSize: PropTypes.number,
  selectedProgram: PropTypes.object,
  siteId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  sortKey: PropTypes.string,
  sortedAndPaginatedLessonCompletionData: PropTypes.oneOfType([
    PropTypes.array,
    PropTypes.object,
    PropTypes.oneOf([null]),
  ]),
  totalItems: PropTypes.number,
  totalPages: PropTypes.number,
}

export default K8Tier2LessonCompletion
